import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../utils/supabaseClient'
import { useAuth } from '../context/AuthContext'
import { Movie, WatchHistory } from '../types'
import VideoPlayer from '../components/VideoPlayer'
import { getSignedM3u8Url, getVideoR2Path } from '../utils/signedUrl'
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, CircularProgress, Alert,
} from '@mui/material'
import { ArrowBack, PlayArrow, History } from '@mui/icons-material'

const MovieDetail: React.FC = () => {
  const { title, episode } = useParams<{ title: string; episode?: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [movies, setMovies] = useState<Movie[]>([])
  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null)
  const [history, setHistory] = useState<Map<string, WatchHistory>>(new Map())
  const [playerSrc, setPlayerSrc] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const historyFetchedRef = useRef(false)

  useEffect(() => {
    if (!title) return
    fetchMovieDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title])

  const fetchMovieDetail = async () => {
    setLoading(true)
    const decodedTitle = decodeURIComponent(title!)
    const { data, error } = await supabase
      .from('movies')
      .select('*')
      .eq('title', decodedTitle)
      .order('episode_number')

    if (error) setError(error.message)
    else if (data && data.length > 0) {
      setMovies(data)
      // If episode param exists, select that episode; otherwise default to first
      if (episode) {
        const epNum = parseInt(episode, 10)
        const selected = data.find(m => m.episode_number === epNum) || data[0]
        setSelectedMovie(selected)
      } else {
        setSelectedMovie(data[0])
      }
    }
    setLoading(false)
  }

  useEffect(() => {
    if (user && movies.length > 0 && !historyFetchedRef.current) {
      fetchHistory()
      historyFetchedRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, movies])

  const fetchHistory = async () => {
    const movieIds = movies.map(m => m.id)
    const { data } = await supabase
      .from('watch_history')
      .select('*')
      .in('movie_id', movieIds)
    if (data) {
      const map = new Map<string, WatchHistory>()
      data.forEach(h => map.set(h.movie_id, h))
      setHistory(map)
    }
  }

  const handlePlay = useCallback(async (movie: Movie) => {
    setSelectedMovie(movie)
    // Update URL to include episode number for refresh persistence
    if (movie.episode_number && title) {
      navigate(`/movie/${encodeURIComponent(title)}/${movie.episode_number}`, { replace: true })
    }
    const r2Path = getVideoR2Path(movie)
    try {
      const url = await getSignedM3u8Url(r2Path, 5 * 3600) // 5小时有效
      setPlayerSrc(url)
    } catch (err) {
      if (err instanceof Error && err.message === 'AUTH_REQUIRED') {
        setError('请先登录后再观看')
      } else {
        setError('获取播放链接失败')
      }
    }
  }, [navigate, title])

  // Stable handleProgress that doesn't change with user/selectedMovie reference updates
  const handleProgress = useCallback(async (position: number, duration: number) => {
    const u = userRef.current
    const m = selectedMovieRef.current
    if (!u || !m) return
    const progress = duration > 0 ? (position / duration) * 100 : 0
    await supabase.from('watch_history').upsert({
      user_id: u.id,
      movie_id: m.id,
      position,
      progress: Math.min(progress, 100),
      last_watched_at: new Date().toISOString(),
    }, { onConflict: 'user_id,movie_id' })
  }, [])

  // Refs to always have latest user and selectedMovie without triggering re-renders
  const userRef = useRef(user)
  const selectedMovieRef = useRef(selectedMovie)

  useEffect(() => {
    userRef.current = user
  }, [user])

  useEffect(() => {
    selectedMovieRef.current = selectedMovie
  }, [selectedMovie])

  const initialPosition = selectedMovie && history.has(selectedMovie.id)
    ? history.get(selectedMovie.id)!.position
    : 0

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
  }

  if (error || movies.length === 0) {
    return <Alert severity="error" sx={{ m: 2 }}>{error || '未找到该内容'}</Alert>
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ mb: 2, color: '#aaa' }}>
        返回
      </Button>

      {playerSrc && selectedMovie && (
        <Box sx={{ mb: 3 }}>
          <VideoPlayer
            src={playerSrc}
            initialPosition={initialPosition}
            onProgress={handleProgress}
          />
          <Typography variant="h6" sx={{ mt: 1, color: '#fff' }}>
            {selectedMovie.title}
            {selectedMovie.episode_number ? ` 第${selectedMovie.episode_number}集` : ''}
          </Typography>
        </Box>
      )}

      {!playerSrc && (
        <Box sx={{
          height: { xs: 200, sm: 400 }, bgcolor: '#1a1a2e', borderRadius: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3,
        }}>
          <Button
            variant="contained" size="large" startIcon={<PlayArrow />}
            onClick={() => {
              const movieToPlay = selectedMovie || movies[0]
              handlePlay(movieToPlay)
            }}
            sx={{ bgcolor: '#e50914', '&:hover': { bgcolor: '#b20710' } }}
          >
            {selectedMovie && selectedMovie.episode_number
              ? `播放第${selectedMovie.episode_number}集`
              : '播放'}
          </Button>
        </Box>
      )}

      <Typography variant="h5" sx={{ mb: 2, color: '#fff' }}>
        {decodeURIComponent(title!)}
        {movies[0].category === 'series' && <Chip label="剧集" size="small" sx={{ ml: 1, bgcolor: '#e50914', color: '#fff' }} />}
      </Typography>

      {movies.length > 1 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, color: '#aaa' }}>选集</Typography>
          <Grid container spacing={1}>
            {movies.map((m, idx) => {
              const h = history.get(m.id)
              const isActive = selectedMovie?.id === m.id
              // Determine episode display number: use episode_number if available, otherwise use index+1
              const epNum = m.episode_number !== null && m.episode_number !== undefined ? m.episode_number : idx + 1
              return (
                <Grid key={m.id} size={{ xs: 4, sm: 3, md: 2, lg: 1 }}>
                  <Card
                    sx={{
                      bgcolor: isActive ? '#e50914' : '#1a1a2e', cursor: 'pointer',
                      '&:hover': { bgcolor: isActive ? '#b20710' : '#2a2a4a' },
                      position: 'relative',
                    }}
                    onClick={() => handlePlay(m)}
                  >
                    <CardContent sx={{ p: 1, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#fff' }}>
                        第{epNum}集
                      </Typography>
                      {h && h.progress > 0 && h.progress < 95 && (
                        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, bgcolor: '#333' }}>
                          <Box sx={{ height: '100%', width: `${h.progress}%`, bgcolor: '#e50914' }} />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </Box>
      )}

      {user && history.size > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, color: '#aaa', display: 'flex', alignItems: 'center', gap: 1 }}>
            <History /> 观看记录
          </Typography>
          {Array.from(history.values()).slice(0, 3).map(h => {
            const m = movies.find(mv => mv.id === h.movie_id)
            if (!m) return null
            return (
              <Box key={h.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#ccc' }}>
                  第{m.episode_number}集 - {Math.round(h.progress)}%
                </Typography>
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export default MovieDetail
